import {
    createProjectSubmit,
    updateProjectSubmit,
} from "../../api/project_api";
import {NewProjectSetup} from "../developer/NewProjectSetup";
import {Address} from "@models/base";
import {Project} from "@models/project";
import {
    withRequiredAuthInfo,
    WithLoggedInAuthInfoProps,
} from "@propelauth/react";
import {useEffect, useState} from "react";
import {useForm, SubmitHandler} from "react-hook-form";
import {useNavigate, useParams} from "react-router-dom";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {useToast} from "../ui/use-toast";
import {USConstructionTypes} from "../../contexts/ConstructionType";


const projectSchema = z.object({
    name: z.string().min(1, "Project name is required"),
    number: z.string().optional(),
    address: z.object({
        address: z.string().optional(),
        coordinates: z.object({
            lat: z.number().optional(),
            lon: z.number().optional(),
        }).nullable().optional()
    }),
    expected_start_date: z.string().optional(),
    expected_duration: z.coerce.number().gte(0, "Expected duration must be at least 0").optional(),
    gsf: z.coerce.number().optional(),
    ROM: z.coerce.number().min(0, "ROM must be at least 0").optional(),
    sector: z.string().optional(),
    sub_sector: z.string().nullable().optional(),
    market: z.string().optional(),
    description: z.string().optional(),
    stage: z.string().optional(),
    website: z.string().optional(),
    // construction_types: z.array(z.string()).optional()
    instructions: z.string().optional(),
    construction_types: z.any().optional()
});

type CreateProjectFormProps = WithLoggedInAuthInfoProps & {
    project_id: string | null;
    project: Project | null;
    editing?: boolean;
    editingEnabled?: boolean;
};

/**
 * TODO
 *  organize the inputs (see design doc)
 *  add validation
 *  Description -> AutosizeTextarea - on its own line
 *
 * Lower Priority
 *  add google map for address (bonus)
 */
function ProjectForm({project, ...props}: CreateProjectFormProps) {
    const {project_id} = useParams<{ project_id: string }>();
    const navigate = useNavigate();
    const {toast} = useToast();
    const form = useForm<Project>({
        resolver: zodResolver(projectSchema),
        defaultValues: project
            ? project
            : {
                project_id: "",
                name: "",
                number: "",
                address: {
                    address: "", coordinates: {lat: 0, lon: 0}
                } as Address,
                website: "",
                expected_start_date: "",
                expected_duration: 0,
                gsf: 0,
                ROM: 0,
                sector: "",
                sub_sector: "",
                market: "",
                description: "",
                stage: "",
                instructions: "",
                construction_types: [],
            },
    });

    const [enabled, setEnabled] = useState<boolean>(true);
    const [hasError, setHasError] = useState<boolean>(false);
    const [errorInfo, setErrorInfo] = useState<string>("");

    const onSubmit: SubmitHandler<Project> = (updatedData: Project) => {
        // console.log(updatedData)

        if (!project) {
            createProjectSubmit(props.accessToken, updatedData)
                .then((updatedData) => {
                    navigate(`/project/${updatedData.project_id}`);
                    toast({
                        title: "Success!",
                        description: "Project created successfully.",
                        variant: "default",
                    });
                })
                .catch((error) => {
                    setHasError(true);
                    setErrorInfo(error.message);
                    toast({
                        title: "Error",
                        description: error.message || "Failed to create Project",
                        variant: "destructive",
                    });
                });
        } else {
            if (!project_id) {
                console.log("project_id is missing");
                return;
            }
            updateProjectSubmit(project_id, props.accessToken, updatedData)
                .then((updatedData) => {
                    form.reset(updatedData);
                    toast({
                        title: "Success!",
                        description: "Project updated successfully.",
                        variant: "default",
                    });
                })
                .catch((error) => {
                    setHasError(true);
                    setErrorInfo(error.message);
                    toast({
                        title: "Error",
                        description: String(error) || "Failed to update project",
                        variant: "destructive",
                    });
                });
        }
    };

    return (
        <div>
            {hasError && <div>{errorInfo}</div>}
            <NewProjectSetup
                methods={form}
                enabled={enabled}
                setEnabled={setEnabled}
                onSubmit={onSubmit}
            />
        </div>
    );
}

export default withRequiredAuthInfo(ProjectForm);
